import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Typography,
  Autocomplete,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';
import axios from 'axios';
import { useNotification } from '../../context/NotificationContext';
import { useOrg } from '../../context/OrgContext';

const RequestInsurancePolicyDialog = ({ open, onClose }) => {
  const { showNotification } = useNotification();
  const { legalName } = useOrg();
  const [spUsers, setSpUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [policyType, setPolicyType] = useState('');
  const [filters, setFilters] = useState({
    city: '',
    state: '',
    zipCode: '',
    country: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [step, setStep] = useState(1); // 1: Select User, 2: Select Policy Type, 3: Confirm

  const policyTypes = [
    'General Liability',
    'Commercial Auto',
    'Workers Compensation',
    'Professional Liability',
    'Garage Liability',
    'Cargo Insurance',
    'Umbrella Policy'
  ];

  // Helper function to get auth headers
  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      window.location.href = '/login';
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  };

  // Fetch SP users
  const fetchSpUsers = async (searchFilters = {}) => {
    setLoading(true);
    setError('');
    try {
      const headers = getAuthHeader();
      if (!headers) return;

      const params = new URLSearchParams({
        role: 'SP',
        fetchForAllVendors: 'true',
        limit: '1000' // Get all SP users
      });

      // Add address filters if provided
      if (searchFilters.city) params.append('city', searchFilters.city);
      if (searchFilters.state) params.append('state', searchFilters.state);
      if (searchFilters.zipCode) params.append('zipCode', searchFilters.zipCode);
      if (searchFilters.country) params.append('country', searchFilters.country);

      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/users?${params.toString()}`, { headers });
      const users = response.data.records || [];
      
      setSpUsers(users);
      setFilteredUsers(users);
    } catch (err) {
      console.error('Error fetching SP users:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to load SP users. Please try again.';
      setError(errorMessage);
      showNotification(`Error loading SP users: ${errorMessage}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Filter users based on current filters
  const applyFilters = () => {
    let filtered = spUsers;

    if (filters.city) {
      filtered = filtered.filter(user => 
        user.address?.city?.toLowerCase().includes(filters.city.toLowerCase())
      );
    }
    if (filters.state) {
      filtered = filtered.filter(user => 
        user.address?.state?.toLowerCase().includes(filters.state.toLowerCase())
      );
    }
    if (filters.zipCode) {
      filtered = filtered.filter(user => 
        user.address?.zip?.includes(filters.zipCode)
      );
    }
    if (filters.country) {
      filtered = filtered.filter(user => 
        user.address?.country?.toLowerCase().includes(filters.country.toLowerCase())
      );
    }

    setFilteredUsers(filtered);
  };

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Send insurance policy request email
  const sendInsurancePolicyRequest = async () => {
    setLoading(true);
    setError('');
    try {
      const headers = getAuthHeader();
      if (!headers) return;

      const requestData = {
        spUserId: selectedUser._id,
        spUserEmail: selectedUser.email,
        spUserName: `${selectedUser.firstName} ${selectedUser.lastName}`,
        companyName: selectedUser.companyName,
        vendorId: selectedUser.vendorNumber || selectedUser.vendorId,
        policyType: policyType,
        requestedBy: legalName
      };

      await axios.post(`${process.env.REACT_APP_API_URL}/api/insurance/request-policy`, requestData, { headers });
      
      const successMsg = 'Insurance policy request sent successfully!';
      setSuccess(successMsg);
      showNotification(successMsg, 'success');
      setStep(3);
    } catch (err) {
      console.error('Error sending insurance policy request:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to send insurance policy request. Please try again.';
      setError(errorMessage);
      showNotification(`Error sending insurance policy request: ${errorMessage}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Reset dialog state
  const resetDialog = () => {
    setSelectedUser(null);
    setPolicyType('');
    setFilters({
      city: '',
      state: '',
      zipCode: '',
      country: ''
    });
    setError('');
    setSuccess('');
    setStep(1);
  };

  // Handle dialog close
  const handleClose = () => {
    resetDialog();
    onClose();
  };

  // Handle next step
  const handleNext = () => {
    if (step === 1 && selectedUser) {
      setStep(2);
    } else if (step === 2 && policyType) {
      sendInsurancePolicyRequest();
    }
  };

  // Handle back step
  const handleBack = () => {
    if (step === 2) {
      setStep(1);
    }
  };

  // Load SP users when dialog opens
  useEffect(() => {
    if (open) {
      fetchSpUsers();
    }
  }, [open]);

  // Apply filters when they change
  useEffect(() => {
    applyFilters();
  }, [filters, spUsers]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Request Insurance Policy
        {step === 1 && ' - Select SP User'}
        {step === 2 && ' - Select Policy Type'}
        {step === 3 && ' - Request Sent'}
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {step === 1 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Select Service Provider
            </Typography>
            
            {/* Filters */}
            <Box sx={{ mb: 3, p: 2, border: '1px solid #eee', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Filter by Location
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="City"
                    value={filters.city}
                    onChange={(e) => handleFilterChange('city', e.target.value)}
                    variant="outlined"
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="State"
                    value={filters.state}
                    onChange={(e) => handleFilterChange('state', e.target.value)}
                    variant="outlined"
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Zip Code"
                    value={filters.zipCode}
                    onChange={(e) => handleFilterChange('zipCode', e.target.value)}
                    variant="outlined"
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Country"
                    value={filters.country}
                    onChange={(e) => handleFilterChange('country', e.target.value)}
                    variant="outlined"
                    size="small"
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Box>

            {/* User Selection */}
            <Autocomplete
              options={filteredUsers}
              getOptionLabel={(option) => {
                const vendorId = option.vendorNumber || option.vendorId || 'N/A';
                const name = `${option.firstName || ''} ${option.lastName || ''}`.trim() || 'N/A';
                const company = option.companyName || 'N/A';
                return `${company} - ${name} (ID: ${vendorId})`;
              }}
              value={selectedUser}
              onChange={(event, newValue) => setSelectedUser(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search by Company Name, User Name, or Vendor ID"
                  variant="outlined"
                  fullWidth
                  placeholder="Type to search..."
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body1" fontWeight="bold">
                      {option.companyName || 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {`${option.firstName || ''} ${option.lastName || ''}`.trim() || 'N/A'} 
                      - Vendor ID: {option.vendorNumber || option.vendorId || 'N/A'}
                    </Typography>
                    {option.address && (
                      <Typography variant="caption" color="text.secondary">
                        {option.address.city}, {option.address.state} {option.address.zip}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
              loading={loading}
              disabled={loading}
            />

            {selectedUser && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Selected Service Provider:
                </Typography>
                <Typography variant="body2">
                  <strong>Company:</strong> {selectedUser.companyName || 'N/A'}
                </Typography>
                <Typography variant="body2">
                  <strong>Contact:</strong> {`${selectedUser.firstName || ''} ${selectedUser.lastName || ''}`.trim() || 'N/A'}
                </Typography>
                <Typography variant="body2">
                  <strong>Email:</strong> {selectedUser.email || 'N/A'}
                </Typography>
                <Typography variant="body2">
                  <strong>Vendor ID:</strong> {selectedUser.vendorNumber || selectedUser.vendorId || 'N/A'}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {step === 2 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Select Policy Type
            </Typography>
            
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Policy Type</InputLabel>
              <Select
                value={policyType}
                onChange={(e) => setPolicyType(e.target.value)}
                label="Policy Type"
              >
                {policyTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Request Summary:
              </Typography>
              <Typography variant="body2">
                <strong>Service Provider:</strong> {selectedUser?.companyName || 'N/A'}
              </Typography>
              <Typography variant="body2">
                <strong>Contact:</strong> {`${selectedUser?.firstName || ''} ${selectedUser?.lastName || ''}`.trim() || 'N/A'}
              </Typography>
              <Typography variant="body2">
                <strong>Policy Type:</strong> {policyType || 'Not selected'}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                An email will be sent to the service provider requesting they provide a Certificate of Insurance (COI) 
                for the selected policy type with {legalName} listed as additionally insured.
              </Typography>
            </Box>
          </Box>
        )}

        {step === 3 && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="h6" color="success.main" gutterBottom>
              Request Sent Successfully!
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              An email has been sent to {selectedUser?.companyName} requesting their {policyType} policy 
              with {legalName} listed as additionally insured.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              The service provider will receive a link to submit their insurance information and upload their COI.
            </Typography>
          </Box>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <CircularProgress />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          {step === 3 ? 'Close' : 'Cancel'}
        </Button>
        {step === 2 && (
          <Button onClick={handleBack}>
            Back
          </Button>
        )}
        {step < 3 && (
          <Button 
            onClick={handleNext} 
            variant="contained"
            disabled={loading || (step === 1 && !selectedUser) || (step === 2 && !policyType)}
          >
            {step === 1 ? 'Next' : 'Send Request'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default RequestInsurancePolicyDialog;